import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import cx from 'classnames';
import Spinner from '@clds/progress';
import Tooltip from '@clds/tooltip';
import t from 'common/i18n';
import SvgIcon from 'root/components/SvgIcon';
import Progress from '../../../root/components/Progress/Progress';
import queueConsts from '../../data/consts';
import { selectPalette } from '../../../root/selectors';
import SVG_ICONS from '~/assets/icons/icons';

const { STATUSES } = queueConsts;

const GAssetStatus = styled.div.attrs(({ error }) => ({
  className: cx('EXPANDED_ITEM--ASSET_STATUS', 'absolute pa3 right-0 top-0 w-100 h-100 flex items-start', {
    'justify-between': error,
    'justify-end': !error,
  }),
}))({
  '& > div:last-child': {
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.5)',
  },
});

const Message = ({ message, palette, type = 'error' }) => {
  const Wrap = styled.div.attrs({ className: cx('f7 mr5 flex-grow') })(({ palette }) => ({
    wordBreak: 'break-all',
    color: palette.textLight,
    zIndex: '999',
  }));
  const Span = styled.span(({ palette }) => ({
    display: 'inline-block',
    backgroundColor: type === 'error' ? palette.error : `${palette.textDark}99`,
    padding: '2px 4px',
  }));

  return (
    <Wrap data-test="error-message" palette={palette}>
      <Span palette={palette}>{message}</Span>
    </Wrap>
  );
};

const preferTranslationText = (status) => [STATUSES.ABORTED, STATUSES.TIMEOUT].includes(status);

export const AssetStatus = ({ status, text, palette, progress }) => {
  const isError = [STATUSES.ERROR, STATUSES.ABORTED, STATUSES.TIMEOUT].includes(status);
  const isTimeout = status === STATUSES.TIMEOUT;
  const translationText = t(`queue.statuses.${status}`);

  const displayText = translationText && preferTranslationText(status) ? translationText : text || translationText;

  return (
    <GAssetStatus data-test="asset-status-icon" error={isError}>
      {isError && (
        <>
          <Message palette={palette} message={displayText} type={status} />
          {!isTimeout && (
            <SvgIcon
              dataTest="error-status-icon"
              path={SVG_ICONS.WARNING_NO_CIRCLE}
              size={16}
              colorName="error-c"
              className="br-100 bg-error flex-none z-2"
            />
          )}
        </>
      )}
      {[STATUSES.UPLOADING, STATUSES.PROCESSING].includes(status) && (
        <div
          data-test="uploading-status"
          style={{ backgroundColor: '#FFF', width: 16, height: 16 }}
          className="br-100 flex items-center justify-center flex-shrink-0"
        >
          {status === STATUSES.UPLOADING ? (
            <Progress size={12} percentage={progress} />
          ) : (
            <Tooltip trigger="hover" title={t('queue.statuses.processing')} placement="top">
              <Spinner.Circular size="sm" variant="primary" />
            </Tooltip>
          )}
        </div>
      )}
      {status === STATUSES.DONE && (
        <SvgIcon dataTest="completed-status-icon" path={SVG_ICONS.DONE_NO_CIRCLE} size={16} colorName="complete-c" className="br-100 bg-complete z-2" />
      )}
      {status === STATUSES.TIMEOUT && (
        <SvgIcon
          dataTest="timeout-status-icon"
          path={SVG_ICONS.INFO_NO_CIRCLE}
          size={16}
          colorName="in-progress-c"
          className="br-100 bg-in-progress z-2"
        />
      )}
    </GAssetStatus>
  );
};

export default connect(
  createStructuredSelector({
    palette: selectPalette,
  })
)(AssetStatus);
